import React, { useEffect, useMemo, useState } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { Spin } from 'antd';

const TINYMCE_CDN =
  'https://cdn.jsdelivr.net/npm/tinymce@5.10.7/tinymce.min.js';

const LOCAL_SCRIPT_CANDIDATES = [
  '/assets/js/tinymce/tinymce.min.js',
  `${process.env.PUBLIC_URL || ''}/tinymce/tinymce.min.js`.replace(
    /([^:]\/)\/+/g,
    '$1',
  ),
];

async function resolveTinymceScriptSrc() {
  for (const url of LOCAL_SCRIPT_CANDIDATES) {
    if (!url) {
      continue;
    }
    try {
      const response = await fetch(url, { method: 'HEAD' });
      if (response.ok) {
        return url;
      }
    } catch {
      /* thử nguồn tiếp theo */
    }
  }

  return TINYMCE_CDN;
}

const editorInitBase = {
  menubar: false,
  branding: false,
  statusbar: true,
  plugins: 'lists link table paste code',
  toolbar:
    'undo redo | formatselect | bold italic underline strikethrough | forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link table | removeformat code',
  paste_as_text: false,
  paste_auto_cleanup_on_paste: false,
  paste_remove_styles_if_webkit: false,
  paste_webkit_styles: 'all',
  paste_data_images: true,
  paste_merge_formats: true,
  paste_convert_word_fake_lists: true,
  paste_word_valid_elements:
    '@[style|class|id|width|height|colspan|rowspan|align|valign|border|cellpadding|cellspacing],b,strong,i,em,u,s,strike,sub,sup,h1,h2,h3,h4,h5,h6,p[style|class],span[style|class],div[style|class],ol[style|class],ul[style|class],li[style|class],a[href|style|target|title],table[style|class|border|cellpadding|cellspacing|width],tr[style|class],td[style|class|colspan|rowspan|width|height],th[style|class|colspan|rowspan],thead,tbody,tfoot,br,hr,img[src|alt|width|height|style]',
  paste_retain_style_properties:
    'color font-size font-family font-weight font-style text-decoration text-align background background-color line-height margin padding border width height',
  verify_html: false,
  valid_elements: '*[*]',
  valid_styles: {
    '*':
      'color,font-size,font-family,font-weight,font-style,text-decoration,text-align,background,background-color,line-height,margin,margin-left,margin-right,margin-top,margin-bottom,padding,padding-left,padding-right,padding-top,padding-bottom,border,border-collapse,border-width,border-style,border-color,width,height,max-width,vertical-align,list-style-type,display,float,white-space',
  },
  entity_encoding: 'raw',
  convert_urls: false,
  content_style:
    'body { font-family: Helvetica, Arial, sans-serif; font-size: 14px; line-height: 1.5; }',
};

const AdminHtmlEditor = ({ value, onChange, id, height = 280 }) => {
  const [scriptSrc, setScriptSrc] = useState(null);
  const editorKey = useMemo(
    () => (scriptSrc ? `${id || 'admin-html'}-${scriptSrc}` : id || 'admin-html'),
    [id, scriptSrc],
  );

  useEffect(() => {
    let cancelled = false;
    resolveTinymceScriptSrc().then((src) => {
      if (!cancelled) {
        setScriptSrc(src);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  if (!scriptSrc) {
    return (
      <div style={{ minHeight: height, display: 'flex', alignItems: 'center' }}>
        <Spin tip="Đang tải trình soạn thảo…" />
      </div>
    );
  }

  return (
    <Editor
      key={editorKey}
      id={id}
      tinymceScriptSrc={scriptSrc}
      value={value || ''}
      rollback={false}
      onEditorChange={(content) => onChange?.(content)}
      init={{
        ...editorInitBase,
        height,
      }}
    />
  );
};

export default AdminHtmlEditor;
